"use client"

/**
 * Homepage v4: the membership-first site's homepage, rendered by
 * `(transparent-nav)/v/[code]/page.tsx` when `isMembershipFirstSite` is on.
 *
 * WHAT THIS IS: a copy of homepage v3 (18.09.2026) that takes the new
 * homepage copy from the Lucis-informed draft
 * (`research/lucis/prototype/home-copy-draft.html`). Sections were ported one
 * at a time from Eugene's list, reading the `homeV4` namespace, which
 * started as a copy of `homeV3`.
 *
 * The layout is v3's: the Figma design "Landing. template" (frame 296:3236,
 * 1512 wide) built section by section on the site's own DS tokens, plus the
 * `type-*-emphasized` rungs added to globals.css for it.
 *
 * WHAT IT IS NOT: the homepage everywhere else. That is
 * `(transparent-nav)/v/[code]/homepage-client.tsx` behind the
 * `homepageHeroFlag` rewrite; nothing here touches it. It renders under
 * `(transparent-nav)` so the hero sits under the real transparent navbar.
 *
 * Order after the 18.09 port (the draft's numbering, not the Figma's): 01
 * hero + 01a trust strip · 04 a full view of your health · 03 sound
 * familiar? (the warm band, the draft's rows in the statistics cards' place)
 * · 02 today is a good day · 03b we keep going (comparison table) · 05
 * everything lands in one plan, twice (the second run carries the "what you
 * get" copy) · 06 who creates your plan · 07 baseline · 08 insurance · 09
 * your rhythm · 10 what people say · 10b stories (fictional placeholders) ·
 * 11 FAQ with three placeholder questions · 12 close as the decision aid.
 * The navbar is the membership-first one, chosen in `PageShell`.
 */

import {
  Fragment,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  type ReactNode,
} from "react"
import Link from "next/link"
import Image from "next/image"
import { blobMediaUrl } from "@/lib/blob-url"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { Button, ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { HomeV4OnePlan } from "./home-v4-one-plan"
import { HomeV4WhatYouGet } from "./home-v4-what-you-get"
import { HomeV4Compare } from "./home-v4-compare"
import { HomeV4Baseline } from "./home-v4-baseline"
import { HomeV4Insurance } from "./home-v4-insurance"
import { HomeV4Practical } from "./home-v4-practical"
import { HomeV4Reviews } from "./home-v4-reviews"
import { HomeV4Stories } from "./home-v4-stories"
import { HomeV4Close } from "./home-v4-close"
import { HomeV4Faq } from "./home-v4-faq"
import { HERO_FX_CSS, HeroRipple } from "./home-v4-hero-fx"
import { HomeV4Familiar } from "./home-v4-familiar"
import type { HomeV4Review } from "./home-v4-reviews-data"
import type { HomeV4Insurer } from "./home-v4-insurers"
import type { PricingV5Data } from "../pricing/pricing-v5-page-data"
import type { HomeV4Footprint } from "./home-v4-page-data"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import { useHomeV4Copy } from "./home-v4-copy"
import { HOME_V4_BLOCK_IDS, type HomeV4BlockId } from "./home-v4-blocks"
import { bookingFlowUrl, membershipAnnualAmount } from "./home-v4-data"
import { useCountry } from "@/components/layout/country-provider"
import { formatCurrency } from "@/lib/format"
import { GoogleRating } from "@/components/ui/google-rating"
import {
  progressiveBlurLayers,
  progressiveLayerMasks,
  FigmaDashIcon,
} from "./home-v4-figma"

export const SECTION = "mx-auto max-w-[1440px] px-4 md:px-10"

/**
 * PAGE ASSETS. Exported from the Figma frame and uploaded to Blob under
 * `images/marketing/home-v3/` (see `scripts/blob-upload-manifest.json`).
 * The hero still is the video's poster frame (296:3239) at 1280×720 — soft
 * when stretched to 1512, but it sits under a 10px blur anyway.
 */
const DEV = blobMediaUrl("/images/marketing/home-v3")
const HERO_STILL = `${DEV}/hero-mri-greeting-poster.jpg`
// Clinic greeting and blood-test sequence; poster matches the opening frame.
const HERO_VIDEO = `${DEV}/hero-clinic-mri-blood-v1.mp4`
// The hero's stills, generated and picked from the tryout (Eugene, 12.10 and
// 13.10), shown in turn. `label` is only for finding a still in this list.
const HERO_DIR = blobMediaUrl("/images/marketing/hero-tryout")
// `phoneX` is the crop on a phone, 0 left to 1 right (HERO_PHONE_X otherwise).
const HERO_SLIDES: {
  label: string
  src: string
  video?: string
  phoneX?: number
}[] = [
  // Eugene's final set (13.10), retouched by hand.
  { label: "Blue · man on cube", src: `${HERO_DIR}/final-01-cube-r2.jpg` },
  {
    label: "Blue · woman stretching",
    src: `${HERO_DIR}/final-03-stretch-blue-r2.jpg`,
    // At the default crop her raised arms ran through the headline and her
    // face was cut at the edge; further right her head is in the frame
    // (Vega and Eugene, bugbash 14.10).
    phoneX: 0.9,
  },
  {
    label: "Blue · man on the floor",
    src: `${HERO_DIR}/final-04-floor-blue-r2.jpg`,
  },
  {
    label: "Sage · woman side stretch",
    src: `${HERO_DIR}/final-05-stretch-sage.jpg`,
  },
  {
    label: "Rose · woman laughing",
    src: `${HERO_DIR}/final-06-laugh-rose.jpg`,
  },
  {
    label: "Peach · woman stretching",
    src: `${HERO_DIR}/final-07-stretch-peach.jpg`,
  },
  {
    label: "Terracotta · grandfather and granddaughter",
    src: `${HERO_DIR}/final-09-shoulders-terracotta.jpg`,
  },
]
/**
 * Switching stills (Eugene, 12.10 and 13.10): each holds for `HERO_HOLD_MS`,
 * then the next comes in over it through a slow ripple from beyond the left
 * edge (`home-v4-hero-fx.tsx`). Reduced motion stays on the first still.
 *
 * `.hero-shift` (Eugene, 13.10): below 1440px wide the cover crop pulls the
 * person left into the headline. From the tablet width up, the picture is
 * made wider than the section, so the subject slides right and the section
 * clips the overhang. The shift grows as the window narrows, to 300px at most.
 * On a phone the copy covers the whole picture, so the crop moves left to
 * bring the person's face into view and a dark veil keeps the copy readable.
 */
const HERO_REVEAL_CSS = `
${HERO_FX_CSS}
@media (max-width: 767px) {
  .hero-shift { object-position: var(--hero-phone-x, 70%) 0% !important; }
}
@media (min-width: 768px) {
  .hero-shift { width: calc(100% + min(300px, max(0px, (1440px - 100vw) * 0.55))) !important; max-width: none; }
}
`
const HERO_HOLD_MS = 7000
/** The phone crop of a still without its own `phoneX`, as `.hero-shift`. */
const HERO_PHONE_X = 0.7
const phoneX = (i: number) => HERO_SLIDES[i]?.phoneX ?? HERO_PHONE_X
const phoneXStyle = (i: number) =>
  ({ "--hero-phone-x": `${phoneX(i) * 100}%` }) as React.CSSProperties
/** Picks the first still at random and sets it before the first paint; the
 *  client reads `window.__heroStart` back. */
const HERO_START_SCRIPT = `(function(){try{var s=${JSON.stringify(
  HERO_SLIDES.map((h) => h.src)
)};var x=${JSON.stringify(
  HERO_SLIDES.map((_, i) => phoneX(i) * 100)
)};var i=Math.floor(Math.random()*s.length);window.__heroStart=i;var e=document.getElementById("hero-still");if(e){e.src=s[i];e.style.setProperty("--hero-phone-x",x[i]+"%")}}catch(_){}})()`
const SHOW_HERO_LAYERS = false
/**
 * Two DIFFERENT pictures of the same person (Eugene, 07.09): the card carries
 * the scene, the avatar carries the face. The card uses the original
 * lake-side running photo IMZellamSee26-99.jpg from Marketing's Drive
 * (Assets/Images/Laura Philipps). The avatar is from her Ahead interview.
 */
const REVIEW_PHOTO = blobMediaUrl(
  "/images/marketing/ambassadors/laura-philipp-running-lake.webp"
)
const REVIEW_AVATAR = blobMediaUrl(
  "/images/marketing/ambassadors/laura-philipp-face.jpg"
)

/**
 * The one rung the DS ladder still has no token for: the hero trust strip's
 * 18/28.5. The h1 is `type-display-xl-emphasized` (a real token now) and the
 * trigger list moved to `type-body-xl`.
 */
/**
 * Section 03: the comet that runs round the card's 2px ring at a constant
 * speed. A conic-gradient did this first but its angular sweep is uneven on
 * a 4:1 rectangle (fast along the long edges, slow round the short ones), so
 * the run is an SVG rect with `pathLength="1"`: dash offsets are fractions
 * of the perimeter and move linearly, so the speed is uniform everywhere.
 *
 * SVG strokes cannot fade along the path, so both ends are stacks of dashes
 * of growing length sharing one endpoint: the tail's all END at the head, the
 * lead's all START there. Coverage — and so opacity — ramps with the number of
 * overlapping layers, in steps too fine to see at 2px. No glow, no dot.
 * Direction is the rect's path direction: clockwise.
 */
const RING_COMET = 0.14 // comet length as a fraction of the perimeter
const RING_LAYERS = 10
// Soft leading edge, as a fraction of the perimeter: ~6-8px at every width the
// card takes (its perimeter runs ~2600-3300px), so the head fades in rather
// than starting on a hard cut.
const RING_LEAD = 0.0024
function FullViewRing() {
  const ref = useRef<SVGSVGElement>(null)

  // Tile ripples. The comet is pure CSS, so this reads its clock back from
  // the CSS animation (`getAnimations`) instead of running its own, and it
  // measures where each tile sits along the perimeter (ResizeObserver), so
  // it holds at every width — at md only the top row overlaps the ring and
  // only those tiles fire. Each pass: a grey outline in the tile's shape
  // scales up and fades, and the tile itself breathes by 2.5%.
  useEffect(() => {
    const svg = ref.current
    const card = svg?.parentElement
    if (!svg || !card) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const anim = svg.getAnimations()[0]
    const path = svg.querySelector("rect")
    if (!anim || !path) return
    const lap = Number(anim.effect?.getComputedTiming().duration)
    if (!(lap > 1000)) return // durations are zeroed under reduced motion
    const total = path.getTotalLength()
    type Stop = { at: number; ripple: HTMLElement; img: HTMLElement }
    let stops: Stop[] = []
    const measure = () => {
      const cr = card.getBoundingClientRect()
      stops = [...card.querySelectorAll<HTMLElement>("[data-ring-tile]")]
        .filter((el) => {
          const r = el.getBoundingClientRect()
          return r.top < cr.top && r.bottom > cr.top // straddles the top edge
        })
        .map((el) => {
          const r = el.getBoundingClientRect()
          // The rect path starts at (x + rx, y), top-left after the corner,
          // and runs right along the top edge. Fires where the head first
          // meets the tile — its LEFT edge, not its middle.
          const s = (r.left - (cr.left + 22)) / total
          return {
            at: ((s % 1) + 1) % 1,
            ripple: el.querySelector<HTMLElement>(".hv3-ripple")!,
            // Pulse the frame, keeping the image's art-directed crop intact.
            img: el.querySelector<HTMLElement>("[data-ring-image-frame]")!,
          }
        })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(card)
    const fire = ({ ripple, img }: Stop) => {
      ripple.animate(
        [
          { transform: "scale(1)", opacity: 0.8 },
          { transform: "scale(1.28)", opacity: 0 },
        ],
        { duration: 1100, easing: "cubic-bezier(0.16, 1, 0.3, 1)" }
      )
      img.animate(
        [
          { transform: "scale(1)" },
          { transform: "scale(1.025)", offset: 0.35 },
          { transform: "scale(1)" },
        ],
        { duration: 900, easing: "ease-out" }
      )
    }
    let last: number | null = null
    let raf = 0
    const tick = () => {
      raf = requestAnimationFrame(tick)
      if (document.visibilityState !== "visible") return
      const t = Number(anim.currentTime)
      if (Number.isNaN(t)) return
      const head = (RING_COMET + ((t / lap) % 1)) % 1
      if (last !== null && head !== last) {
        for (const s of stops) {
          const crossed =
            head >= last
              ? s.at > last && s.at <= head
              : s.at > last || s.at <= head // wrapped past 1 → 0
          if (crossed) fire(s)
        }
      }
      last = head
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [])

  return (
    <svg ref={ref} className="hv3-ring-svg" aria-hidden="true">
      <defs>
        <linearGradient id="hv3-magic" x1="0" y1="0" x2="1" y2="0">
          {/* prettier-ignore */}
          <stop offset="0" stopColor="#94aefb" />
          <stop offset="0.25" stopColor="#d5baff" />
          <stop offset="0.5" stopColor="#9482ff" />
          <stop offset="0.75" stopColor="#bc6dd7" />
          <stop offset="1" stopColor="#a099f0" />{" "}
          {/* ds-color-ignore — the DS magic-stroke gradient, mirrors .pill-magic-stroke in globals.css */}
        </linearGradient>
      </defs>
      {/* Tail: dashes of growing length that all END at the head. */}
      {Array.from({ length: RING_LAYERS }, (_, k) => {
        const len = (RING_COMET * (k + 1)) / RING_LAYERS
        return (
          <rect
            key={`tail-${k}`}
            pathLength={1}
            strokeWidth={2}
            style={{
              strokeDasharray: `${len} ${1 - len}`,
              strokeDashoffset: `calc(var(--hv3-run) + ${len - RING_COMET})`,
              opacity: 0.22,
            }}
          />
        )
      })}
      {/* Leading edge: the same trick mirrored — dashes of growing length that
          all START at the head, so coverage falls from RING_LAYERS to one
          across RING_LEAD and the front fades out instead of cutting. */}
      {Array.from({ length: RING_LAYERS }, (_, k) => {
        const len = (RING_LEAD * (k + 1)) / RING_LAYERS
        return (
          <rect
            key={`lead-${k}`}
            pathLength={1}
            strokeWidth={2}
            style={{
              strokeDasharray: `${len} ${1 - len}`,
              strokeDashoffset: `calc(var(--hv3-run) - ${RING_COMET})`,
              opacity: 0.22,
            }}
          />
        )
      })}
    </svg>
  )
}

function HomeV4Type() {
  return (
    <style>{`
      /* Booking buttons are black on this page, not the DS primary's
         aubergine (Eugene, 11.09). The page's own buttons say so in their
         classes; the navbar is shared with the whole site, so its button
         (desktop bar and the mobile menu's dialog) is overridden here, for
         this route only, until the change is decided for everyone. */
      :is(header,[role=dialog]) .bg-aubergine-800{background-color:var(--color-neutral-950)}
      :is(header,[role=dialog]) .hover\\:bg-aubergine-900:hover{background-color:var(--color-neutral-800)}
      :is(header,[role=dialog]) .focus-visible\\:ring-aubergine-400:focus-visible{--tw-ring-color:var(--color-neutral-400)}
      /* section 01 — chips and checks are visible from the start; once the
         list is half in view each check grows a touch inside its grey chip
         and settles back, top to bottom (Eugene, 04.09). The chip itself does
         not move. Replays on the way back, like the collage's layers. */
      .hv3-tick svg{transform-origin:50% 50%}
      [data-in] .hv3-tick svg{animation:hv3-tick-pop .6s cubic-bezier(.34,1.56,.64,1) var(--d) both}
      @keyframes hv3-tick-pop{0%{transform:scale(1)}45%{transform:scale(1.25)}100%{transform:scale(1)}}
      @media (prefers-reduced-motion:reduce){[data-in] .hv3-tick svg{animation:none}}
      /* section 01 review card: one rounded-rect mask over the whole photo
         group, sized to the card (433x513, r45), no repeat so the 668px box
         outside the card is hidden by the mask, not by an overflow clip. */
      .hv3-review-clip{-webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='433' height='513'%3E%3Crect width='433' height='513' rx='45'/%3E%3C/svg%3E");mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='433' height='513'%3E%3Crect width='433' height='513' rx='45'/%3E%3C/svg%3E");-webkit-mask-size:100% 100%;mask-size:100% 100%;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat}
      .hv3-review-vignette{position:absolute;inset:0;border-radius:45px;box-shadow:inset 0 0 72px rgba(140,172,112,.16),inset 0 -28px 44px -28px rgba(140,172,112,.16)} /* ds-color-ignore — a desaturated grass green lifted off the photograph, not a palette colour; a neutral black inset read as grey dirt on the fade */
      .hv3-mark{--h:var(--phone,34px);display:block;height:var(--h);transform:translateY(calc(var(--h) * var(--dy,0)));background-color:#002147;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-size:contain;mask-size:contain;-webkit-mask-position:left center;mask-position:left center} /* ds-color-ignore — Oxford's own blue, the fill in oxford.svg; all three marks wear it (Eugene, 04.09) */
      @media (min-width:768px){.hv3-mark{--h:34px}}
      .hv3-ground{background-color:#241c14} /* ds-color-ignore — the design's warm ground behind the photograph; DS neutrals are cool greys, so no token matches. Flagged for Eugene rather than inventing one. */
      .hv3-ground-16{background-color:rgba(36,28,20,.16)} /* ds-color-ignore — same ground at 16%, the glass card tint */
      /* Section 03 ring (ultrahuman blood-vision pattern): a 2px base stroke
         (masked ring, same mask as .pill-magic-stroke, follows the 22px
         corners) plus an SVG comet in the DS magic-stroke colours running
         round it — see <FullViewRing>. z-index -1 inside the isolated card
         keeps both under the tiles that overlap the top edge. */
      .hv3-ring{position:relative;isolation:isolate}
      .hv3-ring::before{content:"";position:absolute;inset:0;z-index:-1;border-radius:inherit;padding:2px;pointer-events:none;background:color-mix(in oklab,var(--color-black) 12%,transparent);-webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);-webkit-mask-composite:xor;mask-composite:exclude}
      /* The comet's position along the perimeter, 0 → -1 per lap. Registered so
         the browser interpolates it and every rect's calc() dashoffset follows. */
      @property --hv3-run{syntax:"<number>";initial-value:0;inherits:true}
      @keyframes hv3-run{to{--hv3-run:-1}}
      .hv3-ring-svg{position:absolute;inset:0;z-index:-1;width:100%;height:100%;overflow:visible;pointer-events:none;animation:hv3-run 18s linear infinite}
      .hv3-ring-svg rect{x:1px;y:1px;width:calc(100% - 2px);height:calc(100% - 2px);rx:calc(var(--ring-r,22px) - 1px);fill:none;stroke:url(#hv3-magic)}
      /* Tile ripple: a faint grey outline in the tile's own shape, scaled up and
         faded by <FullViewRing> (WAAPI) as the comet head passes underneath. */
      .hv3-ripple{position:absolute;inset:0;border-radius:28px;border:1.5px solid color-mix(in oklab,var(--color-black) 16%,transparent);opacity:0;pointer-events:none}
    `}</style>
  )
}

/**
 * Section 01's trigger list. Chips and checks are visible from the start;
 * once the list is half in view the checks pop a touch, one after another,
 * top to bottom (Eugene, 04.09). Toggled, not once, so scrolling back replays
 * it the way the collage's layers do.
 */
function TriggerList() {
  const ref = useRef<HTMLUListElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => el.toggleAttribute("data-in", entry.isIntersecting),
      { threshold: 0.5 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])
  const { WHY_NOW } = useHomeV4Copy()
  return (
    <ul ref={ref} className="hv3-ticks flex flex-col gap-5">
      {WHY_NOW.triggers.map((t, i) => (
        <li key={t} className="flex items-start gap-[18px]">
          {/* One line tall, so the dash sits on a reason's first line when
            it wraps rather than at the middle of the block (Eugene, 06.10). */}
          <span
            className="hv3-tick type-body-xl flex h-[1lh] w-[18px] shrink-0 items-center justify-center text-neutral-700"
            style={{ "--d": `${i * 0.14}s` } as React.CSSProperties}
          >
            <FigmaDashIcon />
          </span>
          <p className="type-body-xl text-neutral-900">{t}</p>
        </li>
      ))}
    </ul>
  )
}

/**
 * The hero's foot (Eugene, 15.10). Figma leaves 297px under the CTAs, the
 * trust strip in it. On a shorter window that gap shrinks down to what the
 * strip itself needs; below that the strip goes as a whole, rather than
 * sliding half under the fold, and the column drops its reserve so the hero
 * still fits the screen. Measured, because the headline wraps differently at
 * every width. `null` until measured, and on phones, where the strip is not
 * shown: the CSS default stands.
 */
const HERO_FOOT_MAX = 297
/** Between the CTAs and the strip, at the least. */
const HERO_STRIP_GAP = 40
/** The strip's own `md:pb-[52px]`. */
const HERO_STRIP_BOTTOM = 52
/** The column's `pb-10`, once the strip is gone. */
const HERO_COLUMN_PB = 40

function useHeroFoot() {
  const columnRef = useRef<HTMLDivElement>(null)
  const stripRef = useRef<HTMLUListElement>(null)
  const [foot, setFoot] = useState<number | null>(null)
  useEffect(() => {
    const column = columnRef.current
    const strip = stripRef.current
    if (!column || !strip) return
    const md = window.matchMedia("(min-width: 768px)")
    const measure = () => {
      if (!md.matches) return setFoot(null)
      const cs = getComputedStyle(column)
      const top = parseFloat(cs.paddingTop)
      const content =
        column.getBoundingClientRect().height -
        top -
        parseFloat(cs.paddingBottom)
      const room = Math.floor(window.innerHeight - top - content)
      const need = HERO_STRIP_BOTTOM + strip.offsetHeight + HERO_STRIP_GAP
      setFoot(room >= need ? Math.min(room, HERO_FOOT_MAX) : 0)
    }
    measure()
    // The column's padding is excluded above, so a new foot does not feed
    // back into the next measurement.
    const ro = new ResizeObserver(measure)
    ro.observe(column)
    ro.observe(strip)
    window.addEventListener("resize", measure)
    md.addEventListener("change", measure)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", measure)
      md.removeEventListener("change", measure)
    }
  }, [])
  return { columnRef, stripRef, foot }
}

export default function HomeV4Client({
  insurers,
  reviews,
  planData,
  doctors,
  order,
}: {
  insurers: HomeV4Insurer[]
  reviews: HomeV4Review[]
  planData: PricingV5Data
  doctors: ReviewerExpert[]
  /** Cities and countries with a published clinic, counted in `home-v4-page-data.ts`.
   *  Not shown since the hero's locations link went (Eugene, 23.09); the
   *  loader stays so it can come back. */
  footprint: HomeV4Footprint
  /** Which blocks, in which order; see `home-v4-blocks.ts`. */
  order?: readonly HomeV4BlockId[]
}) {
  const lp = useLocalePath()
  const [heroSlide, setHeroSlide] = useState(0)
  // Each visit opens on a random still (Grégoire, 13.10). The page is built
  // ahead of time, so the pick happens in the browser: an inline script
  // right after the still gives it a random `src` before the first paint,
  // and the client takes that index over here. Until then the still has no
  // `src`, so the first slide is never fetched for nothing.
  const [heroStarted, setHeroStarted] = useState(false)
  // Bumped per transition only, so taking over the random start keeps the
  // still that is already on screen instead of mounting a new one.
  const [heroKey, setHeroKey] = useState(0)
  useEffect(() => {
    const start = (window as { __heroStart?: number }).__heroStart
    if (typeof start === "number" && HERO_SLIDES[start]) setHeroSlide(start)
    setHeroStarted(true)
  }, [])
  const slide = HERO_SLIDES[heroSlide]
  // The still being rippled over while the next one comes in; null at rest.
  const [heroFrom, setHeroFrom] = useState<number | null>(null)
  const [heroLoaded, setHeroLoaded] = useState(false)
  const heroTarget = useRef(0)
  const goSlide = (next: number) => {
    if (next === heroSlide) return
    setHeroFrom(heroSlide)
    setHeroLoaded(false)
    setHeroSlide(next)
    setHeroKey((k) => k + 1)
    // Start the ripple only once the next still is decoded, so it never
    // sweeps in an empty frame. `onLoad` misses stills already in the cache.
    // Fetched with CORS, as the stills and the canvas fetch it, so the three
    // share one download.
    const img = document.createElement("img")
    img.crossOrigin = "anonymous"
    img.src = HERO_SLIDES[next].src
    const ready = () => {
      if (heroTarget.current === next) setHeroLoaded(true)
    }
    heroTarget.current = next
    img.decode().then(ready, ready)
    window.setTimeout(ready, 3000)
  }
  const stepSlide = (d: number) =>
    goSlide((heroSlide + d + HERO_SLIDES.length) % HERO_SLIDES.length)
  const advanceSlide = useEffectEvent(() => stepSlide(1))
  // Hold each still, then ripple to the next; reduced motion stays put.
  useEffect(() => {
    if (heroFrom !== null) return
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const t = window.setTimeout(advanceSlide, HERO_HOLD_MS)
    return () => window.clearTimeout(t)
  }, [heroSlide, heroFrom])
  const { HERO, TRUST, WHY_NOW, FULL_VIEW, DOCTORS } = useHomeV4Copy()
  const heroFoot = useHeroFoot()
  // "Start from {price}/year": the plan's yearly price from the prices the
  // server read for the page, so the first paint and hydration agree.
  const { code: country } = useCountry()
  const planPrice = formatCurrency(
    membershipAnnualAmount(planData.initialPrices?.prices),
    country
  )
  // Section 06's people. Names and photographs are the CMS's; the caption
  // under each name is the page's two-line "institution · specialisation"
  // (see `DOCTORS.captions`), in the page's language. Anna leads with the
  // statement and wears the About page's studio portrait, and is not
  // repeated in the grid (Eugene, 18.09); Dr. Komaromi takes her card.
  // Donati and Fischer, the founding medical partners, open the grid as they
  // do in the Figma; the rest keep the CMS's order.
  const surnames = Object.keys(
    DOCTORS.captions
  ) as (keyof typeof DOCTORS.captions)[]
  const people = doctors.map((d) => {
    const surname = surnames.find((k) => d.name.includes(k))
    return {
      ...d,
      // A featured expert the page has no caption for shows the CMS bio,
      // which carries a stray line break in one profile.
      title: surname ? DOCTORS.captions[surname] : d.title.replace(/\s+/g, " "),
      image: d.name.includes("Erat")
        ? blobMediaUrl(DOCTORS.lead.photo)
        : d.image,
    }
  })
  const lead = people.find((d) => d.name.includes("Erat"))
  const pinned = ["Donati", "Fischer"]
    .map((s) => people.find((d) => d.name.includes(s)))
    .filter((d): d is ReviewerExpert => Boolean(d))
  const cards = [
    ...pinned,
    ...people.filter((d) => !pinned.includes(d) && d !== lead),
  ]

  // Every block by id, so `order` can rearrange or drop them.
  // Without an order the page renders as built (`HOME_V4_BLOCK_IDS`).
  const blocks: Record<HomeV4BlockId, ReactNode> = {
    hero: (
      <>
        {/* ── 00. Hero — Figma 296:3239..3248, 301:881. Full-bleed still under
         the transparent navbar; copy column at the frame's left margin, trust
         strip pinned to the foot. Figma's frame is 1013 tall on a 16" MacBook,
         i.e. one viewport, so the section is viewport-high and the column
         sits where the frame puts it rather than centred. ── */}
        <section className="relative isolate flex min-h-[100svh] flex-col justify-end overflow-hidden bg-neutral-900 text-white">
          <style>{HERO_REVEAL_CSS}</style>
          {heroFrom !== null && (
            <Image
              src={HERO_SLIDES[heroFrom].src}
              alt=""
              fill
              unoptimized
              crossOrigin="anonymous"
              sizes="100vw"
              style={phoneXStyle(heroFrom)}
              className="hero-shift object-cover object-[100%_0%]"
            />
          )}
          <div
            key={heroKey}
            className="hero-reveal absolute inset-0"
            data-state={
              heroFrom === null ? undefined : heroLoaded ? "run" : "wait"
            }
            onAnimationEnd={(e) => {
              if (e.target === e.currentTarget) setHeroFrom(null)
            }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- the src is
                picked by HERO_START_SCRIPT before React runs */}
            <img
              id={heroKey === 0 ? "hero-still" : undefined}
              src={heroStarted ? slide.src : undefined}
              style={heroStarted ? phoneXStyle(heroSlide) : undefined}
              alt=""
              crossOrigin="anonymous"
              fetchPriority="high"
              decoding="async"
              suppressHydrationWarning
              className="hero-shift absolute inset-0 h-full w-full object-cover object-[100%_0%]"
            />
            {slide.video && (
              <video
                key={slide.video}
                src={slide.video}
                poster={slide.src}
                autoPlay
                muted
                loop
                playsInline
                aria-hidden
                style={phoneXStyle(heroSlide)}
                className="hero-shift absolute inset-0 h-full w-full object-cover object-[100%_0%]"
              />
            )}
          </div>
          <script
            dangerouslySetInnerHTML={{ __html: HERO_START_SCRIPT }}
            suppressHydrationWarning
          />
          {heroFrom !== null && heroLoaded && (
            <HeroRipple
              key={`ripple-${slide.src}`}
              from={HERO_SLIDES[heroFrom].src}
              to={slide.src}
              fromPhoneX={phoneX(heroFrom)}
              toPhoneX={phoneX(heroSlide)}
              onDone={() => setHeroFrom(null)}
            />
          )}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-neutral-950/20 via-neutral-950/40 to-neutral-950/55 md:hidden"
          />
          {SHOW_HERO_LAYERS && (
            <>
              {/* Same crop as the still so the poster→video swap is invisible.
            `motion-reduce:hidden` rather than a matchMedia gate to keep this
            file's diff minimal — the tradeoff is a hidden video still
            decoding for reduced-motion users. */}
              <video
                src={HERO_VIDEO}
                poster={HERO_STILL}
                autoPlay
                muted
                loop
                playsInline
                aria-hidden
                className="absolute inset-0 h-full w-full object-cover object-[62%_30%] motion-reduce:hidden md:object-[50%_30%]"
              />
              {/* Figma 296:3240 — BACKGROUND_BLUR, blurType PROGRESSIVE, radius
            20.6 (= 10.3px CSS), sharp at x 63.6% and full strength at x 2.8%.
            So the frame is only softened under the copy column and the
            subject on the right stays sharp; a flat blur over the whole
            photograph is a different picture entirely. Desktop only — on a
            phone the copy sits over the middle of a vertical crop. */}
              <div className="absolute inset-0 hidden md:block" aria-hidden>
                {progressiveBlurLayers({
                  radius: 10.3,
                  from: 36.4,
                  to: 97.2,
                  direction: "to left",
                }).map((style, i) => (
                  <div key={i} className="absolute inset-0" style={style} />
                ))}
              </div>
              <div
                className="absolute inset-0 backdrop-blur-[4px] md:hidden"
                aria-hidden
              />
              {/* Figma 296:3241 — linear scrim at 66% layer opacity: black 0.8 → 0.58
            at 40% → 0 at 56%, left to right. Multiplied out, that is the
            0.53 / 0.38 / 0 below. */}
              <div
                className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/40 via-40% to-transparent to-[56%]"
                aria-hidden
              />
              {/* Figma 296:3242 — a 237px band at the foot of the 1013 frame:
            black 0.75 → 0.35 at 55% → 0. Taller on a phone, where the copy
            column sits inside it rather than above it. */}
              <div
                className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-black/75 via-black/35 via-55% to-transparent md:h-[237px]"
                aria-hidden
              />
              {/* Navbar band — not in the design; keeps the white nav legible on
            the bright top of the frame. */}
              <div
                className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/40 to-transparent"
                aria-hidden
              />
            </>
          )}

          <div className={`${SECTION} relative z-10 w-full`}>
            {/* Figma: eyebrow y=345, h1 y=382 (h 169), lede y=573, CTAs y=664,
              trust y=903, frame 1013 — so the column ends 349px above the
              foot and the strip 52px above it. */}
            <div
              ref={heroFoot.columnRef}
              className="flex max-w-[674px] flex-col items-start pt-40 pb-10 md:pb-[297px]"
              style={
                heroFoot.foot === null
                  ? undefined
                  : { paddingBottom: heroFoot.foot || HERO_COLUMN_PB }
              }
            >
              {/* Stars only; the rating and the check-up count show on
                hover (Eugene, 23.09: remove the 5, show more on hover). */}
              <GoogleRating
                variant="onDark"
                staticOnDark
                labelOnHover
                rating="5.0"
                reviewLabel={HERO.rating}
                size="md"
                className="!self-start"
              />
              {/* One colour for the whole headline (Eugene, 22.09: remove the
                green on the second half). */}
              <h1 className="type-display-xl-emphasized mt-[21px] text-balance text-white">
                {HERO.heading.plain}
                {HERO.heading.accent}
              </h1>
              <p className="type-title-lg mt-[22px] max-w-[590px] font-normal text-white/85">
                {HERO.body}
              </p>
              <div className="mt-[35px] flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-[22px]">
                <BookingCta
                  productId={PRODUCT_IDS.bloodTest}
                  step="product"
                  overrideHref={bookingFlowUrl()}
                  trackingLabel="home_v3_cta"
                  trackingLocation="dev/home-v4"
                  className="block w-full sm:inline-block sm:w-auto"
                >
                  {/* Figma 296:3251: white pill, black label, arrow at the
                    end. `primary` is aubergine, so only the colours are
                    overridden; metrics stay the DS `lg` button. */}
                  <Button
                    size="lg"
                    variant="primary"
                    className="group w-full bg-white text-neutral-950 hover:bg-neutral-100 hover:shadow-none sm:w-auto"
                    iconRight={
                      <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                    }
                  >
                    {HERO.ctaPrimary(planPrice)}
                  </Button>
                </BookingCta>
                <Link
                  href={lp("/how-it-works")}
                  className={cn(
                    buttonVariants({ variant: "text", size: "lg" }),
                    "bg-white/30 text-white backdrop-blur-[2px] hover:bg-white/40",
                    "w-full sm:w-auto"
                  )}
                >
                  {HERO.ctaSecondary}
                </Link>
              </div>
              {/* No "19 cities in 4 countries" link under the CTAs
                (Eugene, 23.09: remove). */}
            </div>
          </div>

          {/* ── 01a. Trust strip — Figma 301:881 at (84, 903), 821×58, claims
            at 18/28.5/-0.152 with a 1px white line at 32% opacity between
            them, 58 tall, 32px after the text it follows and 26px before the
            next one. Verifiable facts, not qualities (the draft's L8), each
            ONE line of text rather than a title and a subtitle (Eugene,
            18.09). Three since 22.09: the 5–7 days went (Eugene: the
            timing is not needed here), the doctor's plan stayed. The columns
            are a fixed width from `lg`; below it the three share the row.
            Not on phones (Eugene, 06.10). ── */}
          <div
            className={cn(
              "relative z-10 hidden md:absolute md:inset-x-0 md:bottom-0 md:block md:pb-[52px]",
              heroFoot.foot === 0 && "md:invisible"
            )}
            aria-hidden={heroFoot.foot === 0 || undefined}
          >
            <div className={SECTION}>
              <ul
                ref={heroFoot.stripRef}
                className="flex flex-col gap-4 md:flex-row md:items-center"
              >
                {TRUST.map((claim, i) => (
                  <li
                    key={claim}
                    className="flex items-center md:h-[58px] md:flex-1 lg:flex-none"
                  >
                    <p className="type-body-lg text-white md:flex-1 lg:w-[235px] lg:flex-none">
                      {claim}
                    </p>
                    {i < TRUST.length - 1 ? (
                      <span
                        className="hidden h-[58px] w-px bg-white/32 md:mr-[26px] md:ml-8 md:block"
                        aria-hidden
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </>
    ),
    fullView: (
      <>
        {/* ── 03. A full view of your health — Figma 306:7659: a 1316×309 card
         outlined at 1.4px black 19%, radius 22, with four 128px tiles hung
         over its top edge and the four columns (310:8213) inside it. The band
         is white, 2490→3470 in the frame. On a wide screen the tiles are 160
         (Eugene, 13.10), still centred on the edge, the titles where they
         were. ── */}
        <section className="bg-white pt-10 pb-10 md:pt-[85px] md:pb-[84px]">
          <div className={SECTION}>
            <p className="type-label-lg text-neutral-500">
              {FULL_VIEW.eyebrow}
            </p>
            {/* h2 left, lede right — the Figma puts the lede at x=751 of 1512,
              which is where a two-column grid lands it anyway. The lede says
              what you do and how the four columns add up (Eugene, 22.09:
              rewrite rather than remove). */}
            <div className="mt-3 grid grid-cols-1 gap-4 md:mt-[27px] md:grid-cols-2 md:gap-6">
              <h2 className="type-display-md-emphasized max-w-[386px] text-neutral-900">
                {FULL_VIEW.heading.plain}
                <br />
                <span className="text-deep-green-600">
                  {FULL_VIEW.heading.accent}
                </span>
              </h2>
              <p className="type-body-xl max-w-[483px] text-neutral-600">
                {FULL_VIEW.lede}
              </p>
            </div>

            {/* The tiles are children of their columns rather than absolutely
              positioned: each is pulled up by the card's own top padding plus
              the 62px it overhangs, so the overhang survives a text reflow.
              The pull is `lg`-only — stacked at one or two columns, a second
              row of tiles would climb into the text above it.
              Figma's inner padding is lopsided (51 left, 85 right) — the grid
              divides the card evenly instead, which reads the same and does
              not fall apart at other widths. */}
            <div className="hv3-ring mt-8 rounded-[40px] p-5 pb-8 [--ring-r:40px] md:mt-[115px] md:rounded-[22px] md:px-6 md:pt-0 md:pb-12 md:[--ring-r:22px] lg:px-[51px] lg:pt-[109px] lg:pb-[52px]">
              <FullViewRing />
              <ul className="relative grid grid-cols-1 gap-x-[60px] gap-y-10 md:auto-rows-fr md:grid-cols-2 md:gap-y-[128px] md:before:absolute md:before:inset-x-[-24px] md:before:top-[calc(50%+64px)] md:before:border-t-2 md:before:border-black/12 lg:auto-rows-auto lg:grid-cols-4 lg:gap-y-16 lg:before:hidden">
                {FULL_VIEW.columns.map((col) => (
                  <li key={col.title} className="flex flex-col">
                    <div
                      data-ring-tile
                      className="relative mb-4 size-32 md:mt-[-64px] md:mb-6 lg:mt-[-187px] lg:mb-[27px] lg:size-40"
                    >
                      <span className="hv3-ripple" aria-hidden="true" />
                      <div
                        data-ring-image-frame
                        className="relative size-32 overflow-hidden rounded-[20px] md:rounded-[28px] lg:size-40 lg:rounded-[34px]"
                      >
                        <Image
                          src={
                            col.tile.startsWith("/")
                              ? blobMediaUrl(col.tile)
                              : `${DEV}/${col.tile}`
                          }
                          alt=""
                          width={320}
                          height={320}
                          className="relative size-32 object-cover lg:size-40"
                          style={
                            col.tile.endsWith("blood-tubes-close.webp")
                              ? { transform: "translate(-2%, 17%) scale(1.4)" }
                              : undefined
                          }
                        />
                      </div>
                    </div>
                    {/* One title per column, no eyebrow above it (Eugene,
                      22.09): the eyebrow's verb went into the title. Every title
                      wraps to two lines (Eugene, 22.09): the width cap breaks the
                      short ones on wide screens, and min-h holds two lines if a
                      translation still fits in one, so the bodies line up. */}
                    <h3 className="type-headline-sm-emphasized text-neutral-900 md:min-h-[2lh] md:max-w-[220px]">
                      {col.title}
                    </h3>
                    <p className="type-body-lg mt-2 text-neutral-600 md:mt-3">
                      {col.body}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <p className="type-headline-lg-emphasized mx-auto mt-12 max-w-[490px] text-center text-neutral-900 md:mt-[68px]">
              {FULL_VIEW.closing}
            </p>
            <div className="mt-8 flex justify-center md:mt-9">
              {/* Booking on the black button, as under why now, in place
                  of "Our process" (Eugene, 30.09). */}
              <BookingCta
                productId={PRODUCT_IDS.bloodTest}
                step="product"
                overrideHref={bookingFlowUrl()}
                trackingLabel="home_v4_full_view_cta"
                trackingLocation="dev/home-v4"
                className="block w-full sm:inline-block sm:w-auto"
              >
                <Button
                  size="lg"
                  variant="primary"
                  className="group w-full bg-neutral-950 focus-visible:ring-neutral-400 sm:w-auto"
                  iconRight={
                    <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                  }
                >
                  {FULL_VIEW.bookCta}
                </Button>
              </BookingCta>
            </div>
          </div>
        </section>
      </>
    ),
    familiar: (
      <>
        {/* ── 03. Sound familiar? — a carousel over lifestyle photos since
         23.09 (Eugene); the warm band and its frosted cards are gone. See
         `home-v4-familiar.tsx`. ── */}
        <HomeV4Familiar />
      </>
    ),
    whyNow: (
      <>
        {/* ── 01. Today is a good day to start — Figma 303:913 (review card,
         433×513 at x=237) + 303:937 (copy column, 545 wide at x=778, 47px
         lower than the card). White band, 133px above / 102px below. ── */}
        {/* No white rectangle behind this band in the frame — it sits on the
          frame's own #f6f6f6 ground (the site's `bg-background`), as do 06
          and 08. Only 03, 05, 07 and 09 carry a white band. */}
        <section className="bg-background pt-10 pb-10 md:pt-[133px] md:pb-[102px]">
          <div
            className={`${SECTION} grid grid-cols-1 gap-12 lg:grid-cols-[594px_minmax(0,1fr)] lg:gap-[108px]`}
          >
            {/* Review card — Figma 303:913, 433×513, radius 45. The photo
              (314:9757) is a 668px box at (-141,-59) whose fill is CROPped,
              under a white gradient, and carries a PROGRESSIVE LAYER blur:
              radius 19.4 (= 9.7px CSS), sharp until 52.7% of the box height
              and full strength by 93.8%. The face stays sharp, the paper the
              quote sits on does not. */}
            {/* The photo, its blurred copies and the white fade are masked to the
              card's rounded rect as ONE group (.hv3-review-clip). Clipping the
              figure instead (overflow or clip-path) anti-aliases each layer's
              edge separately, and on the corner pixels the dark photo shows
              through under the fade as a thin rim. Measured, not guessed: hide
              the fade and the rim triples, hide the photo box and it is gone. */}
            <figure className="relative h-[513px] w-full max-w-[433px] rounded-[45px] bg-neutral-50 lg:ml-[161px]">
              <div className="hv3-review-clip absolute inset-0">
                <div className="absolute top-[-59px] left-[-141px] size-[668px]">
                  <Image
                    src={REVIEW_PHOTO}
                    alt=""
                    width={668}
                    height={668}
                    className="absolute inset-0 size-full max-w-none object-cover"
                    aria-hidden
                  />
                  {progressiveLayerMasks({
                    radius: 9.7,
                    from: 52.7,
                    to: 93.8,
                    direction: "to bottom",
                  }).map((style, i) => (
                    <Image
                      key={i}
                      src={REVIEW_PHOTO}
                      alt=""
                      width={668}
                      height={668}
                      className="absolute inset-0 size-full max-w-none object-cover"
                      style={style}
                      aria-hidden
                    />
                  ))}
                  {/* Figma's white fade over the same box. */}
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage:
                        "linear-gradient(179.4deg, rgba(255,255,255,0) 36.073%, rgba(255,255,255,0.65) 51.81%, rgba(255,255,255,0.89) 76.987%, var(--color-neutral-50) 89.576%)",
                    }}
                    aria-hidden
                  />
                </div>
                {/* The card fades into `neutral-50`, which IS the page background
                (#f6f6f6 both), so without this the bottom half has no edge at
                all and the card dissolves into the page. The vignette runs the
                whole height and carries a second, shorter inset along the
                bottom, so the shape closes and the lower corners read (Eugene,
                07.09). Its colour is a desaturated grass green picked off the
                photograph rather than black, which read as grey dirt. Strength
                is measured, not guessed: at the bottom edge it takes the page's
                246,246,246 to 234,238,230 — green enough to see the hue, light
                enough that the edge is felt and not noticed. */}
                <div className="hv3-review-vignette" aria-hidden />
              </div>
              {/* Figma's coordinates are for the 433px card; on a phone the
                quote and the caption hang from the bottom instead, so a
                narrower card wraps the quote upwards (Eugene, 06.10).
                The quotation marks live in the catalogue, not here: each
                language sets its own pair ("…" / „…" / «…»), and a dash in
                front of a quote is not punctuation in any of them (Michèle,
                AHD-11317). */}
              <blockquote className="type-body-xl-emphasized absolute inset-x-6 bottom-[118px] text-neutral-900 md:inset-x-auto md:top-[324px] md:bottom-auto md:left-[43px] md:w-[371px]">
                {WHY_NOW.quote}
              </blockquote>
              <figcaption className="absolute bottom-7 left-6 flex items-center gap-[18px] md:top-[434px] md:bottom-auto md:left-[43px] md:gap-[14px]">
                {/* On a phone the role breaks after the dot, so the caption
                  is three lines and the photo grows to their height
                  (Eugene, 06.10). */}
                <Image
                  src={REVIEW_AVATAR}
                  alt=""
                  width={80}
                  height={80}
                  className="size-[62px] rounded-full object-cover md:size-10"
                />
                <div className="flex flex-col gap-1.5">
                  <p className="type-body-md text-neutral-900">
                    {WHY_NOW.author}
                  </p>
                  <p className="type-body-md text-neutral-500">
                    {WHY_NOW.role.split(" · ").map((part, i) => (
                      <Fragment key={part}>
                        {i > 0 && (
                          <>
                            {" · "}
                            <br className="md:hidden" />
                          </>
                        )}
                        {part}
                      </Fragment>
                    ))}
                  </p>
                </div>
              </figcaption>
            </figure>

            <div className="flex flex-col items-start gap-8 md:self-center">
              {/* No lede under the heading any more (Eugene, 18.09): the three
                reasons below say it. Centred on the card (Eugene, 22.09)
                instead of Figma's 47px drop from its top. */}
              <h2 className="type-display-md-emphasized max-w-[410px] text-neutral-900">
                {WHY_NOW.heading}
              </h2>
              {/* Figma 303:915 / 296:3396: 34px chips at radius 11 on
                neutral-900 at 8%, an 18px check, 18px to the line, 12px below
                each row and 12px between them. The Figma sets the trigger at
                20/28; Eugene's call is body-xl (18/24), which is on the
                ladder — so this is one place the build deliberately differs
                from the file. */}
              <TriggerList />
              {/* No closing line under the list (Eugene, 22.09: removed
                "Whichever it is, you get an answer and a plan"); the list
                runs straight into the button. */}
              <BookingCta
                productId={PRODUCT_IDS.bloodTest}
                step="product"
                overrideHref={bookingFlowUrl()}
                trackingLabel="home_v3_why_now_cta"
                trackingLocation="dev/home-v4"
                className="block w-full sm:inline-block sm:w-auto"
              >
                <Button
                  size="lg"
                  variant="primary"
                  className="group w-full bg-neutral-950 focus-visible:ring-neutral-400 sm:w-auto"
                  iconRight={
                    <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                  }
                >
                  {HERO.ctaPrimary(planPrice)}
                </Button>
              </BookingCta>
            </div>
          </div>
        </section>
      </>
    ),
    compare: (
      <>
        {/* ── 03b. Compared — Ahead against a standard check-up, right after
         the reasons: the rows above say what people come with, the table
         says why a check-up answers it. Eugene (18.09): not sure it is
         needed, in for now; 22.09: re-cut after the NOA table. ── */}
        <HomeV4Compare section={SECTION} planPrice={planPrice} />
      </>
    ),
    onePlan: (
      <>
        {/* ── 05. Everything lands in one plan — a two-stage pinned reveal
         rather than a static band; see `home-v4-one-plan.tsx`. ── */}
        <HomeV4OnePlan section={SECTION} />
      </>
    ),
    whatYouGet: (
      <>
        {/* ── 05b. What you get — 05's two beats with Figma 473:2359's five
         tiles (Eugene, 29.09): plan, trend, report and follow-ups. 05 itself
         left the page the same day; it stays built as the parked `onePlan`
         block. See `home-v4-what-you-get.tsx`. ── */}
        <HomeV4WhatYouGet section={SECTION} />
      </>
    ),
    doctors: (
      <>
        {/* ── 06. Who creates your plan — Figma 314:11761: a 906px band,
         on the page grey with white doctor cards since 07.10 (Eugene). Lead
         quote with a 108px portrait, then a 3×2 grid of doctor cards, then
         the university marks. The people are the
         CMS's featured reviewers, the six the live homepage shows. ── */}
        {cards.length > 0 && (
          <section className="bg-background pt-10 pb-10 md:pt-[92px] md:pb-[115px]">
            <div className={SECTION}>
              <p className="type-label-lg text-neutral-500">
                {DOCTORS.eyebrow}
              </p>
              <h2 className="type-display-md-emphasized mt-[11px] max-w-[640px] text-neutral-900">
                {DOCTORS.heading}
              </h2>

              {/* Figma 314:11764: the 108px portrait, then a 820px column,
                quote first and attribution 124px down. The file has the
                column 24px clear of the portrait; here it is 40px, the same
                as the page padding on the portrait's other side, so the
                portrait sits in equal air (Eugene, 10.09). The card grid
                and the marks below are indented to match. */}
              {lead && (
                <figure className="mt-10 flex flex-col gap-6 md:mt-[60px] md:flex-row md:gap-10">
                  <Image
                    src={lead.image}
                    alt={lead.name}
                    width={108}
                    height={108}
                    className="size-[108px] shrink-0 rounded-[12px] object-cover"
                  />
                  <div className="max-w-[820px]">
                    {/* headline-md at weight 400 — the DS rung is medium, and the
                      design sets this one Regular, which is what keeps three
                      lines from shouting over the h2 above them. A statement
                      in the team's voice rather than a quotation (Eugene,
                      18.09), so no quotation marks: how the plan is made,
                      which used to be the lede under the heading. */}
                    <p className="type-headline-md font-normal text-neutral-900">
                      {DOCTORS.lead.statement}
                    </p>
                    <figcaption className="mt-4">
                      <p className="type-body-lg-emphasized text-neutral-950">
                        {lead.name}
                      </p>
                      <p className="type-body-md mt-1 text-neutral-500">
                        {lead.title}
                      </p>
                    </figcaption>
                  </div>
                </figure>
              )}

              {/* Figma 324:13293 — 1142 wide. The line everything aligns to is
                the QUOTE TEXT, not the column (Eugene, 04.09): the portrait's
                108px plus its 40px gap. Each card's photograph sits on that
                line, so the card itself starts one padding (12px) left of
                it, and the space under the lead portrait stays empty. Phones
                stack the portrait over the quote, so the indent exists only
                from `md`. Two columns on a phone, each card stacked — portrait
                on top, name and bio beneath; desktop keeps the design's
                horizontal card. The bios are the CMS's full sentences, so
                the cards grow with them rather than clipping. */}
              <ul className="mt-12 grid max-w-[1142px] grid-cols-2 gap-[18px] md:mt-[52px] md:ml-[136px] lg:grid-cols-3">
                {cards.map((doc) => (
                  <li
                    key={doc.name}
                    className="flex flex-col gap-3 rounded-[20px] bg-white p-3 md:flex-row md:items-center md:gap-[18px]"
                  >
                    <Image
                      src={doc.image}
                      alt={doc.name}
                      width={220}
                      height={220}
                      sizes="(min-width: 768px) 72px, 45vw"
                      className="aspect-square h-auto w-full shrink-0 rounded-[12px] object-cover md:aspect-auto md:h-[72px] md:w-[72px]"
                    />
                    <div className="flex flex-col gap-1">
                      <p className="type-body-lg-emphasized text-neutral-950">
                        {doc.name}
                      </p>
                      <p className="type-body-md text-neutral-500">
                        {doc.title}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Figma 326:13375 — the marks start on the quote's line, under
                the card photographs. All three in Oxford's blue (Eugene): the
                files are Oxford's #002147 and two 45% greys, so each vector
                is a CSS mask over one colour rather than a redrawn logo. */}
              <ul className="mt-8 flex max-w-[1142px] items-center justify-between gap-4 md:mt-[28px] md:ml-[148px] md:flex-wrap md:justify-start md:gap-8">
                {DOCTORS.logos.map((logo) => (
                  <li key={logo.alt}>
                    <span
                      role="img"
                      aria-label={logo.alt}
                      className="hv3-mark"
                      style={{
                        aspectRatio: logo.ratio,
                        // The lift is a share of the height, so it scales
                        // with the smaller phone marks.
                        ["--phone" as string]: `${logo.phone}px`,
                        ["--dy" as string]: logo.dy / 34,
                        WebkitMaskImage: `url(${blobMediaUrl(logo.src)})`,
                        maskImage: `url(${blobMediaUrl(logo.src)})`,
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </>
    ),
    baseline: (
      <>
        {/* ── 07. Start with your baseline — Figma 314:10569 ── */}
        <HomeV4Baseline section={SECTION} planData={planData} />
      </>
    ),
    insurance: (
      <>
        {/* ── 08. Supplementary insurance — Figma 314:10935 ── */}
        <HomeV4Insurance section={SECTION} insurers={insurers} />
      </>
    ),
    practical: (
      <>
        {/* ── 09. Your rhythm — the draft's cadence (Day 1 · Day 5–7 · As
         needed · Every 12 months) on the v3 deck ── */}
        <HomeV4Practical section={SECTION} />
      </>
    ),
    reviews: (
      <>
        {/* ── 10. Real results from real people — Figma 329:15822 ── */}
        <HomeV4Reviews section={SECTION} reviews={reviews} />
      </>
    ),
    stories: (
      <>
        {/* ── 10b. Stories — the same rail again, with fictional narrative
         placeholders; see home-v4-stories.tsx. ── */}
        <HomeV4Stories section={SECTION} />
      </>
    ),
    faq: (
      <>
        {/* ── 11. FAQ — not in the Figma frame. The site's nine `home.faq*`
         questions plus the draft's three, restyled onto this page's rungs;
         see home-v4-faq.tsx. ── */}
        <HomeV4Faq section={SECTION} />
      </>
    ),
    close: (
      <>
        {/* ── 12. Not sure where to start? — Figma 336:1760, the quiz as the
         close ── */}
        <HomeV4Close section={SECTION} />
      </>
    ),
  }

  return (
    <>
      <HomeV4Type />
      {(order ?? HOME_V4_BLOCK_IDS).map((id) => (
        <Fragment key={id}>{blocks[id]}</Fragment>
      ))}
    </>
  )
}
